import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchMyOrders,
  fetchMyOrdersFilters,
  fetchMyOrdersStatusCounts,
  acknowledgeOrder,
  upsertOrderServiceDetails,
  upsertOrderRequestComments,
} from "../services/engagementManager.service";
import { fetchAllPages } from "../../util/helpers";

const initialState = {
  rows: [],
  totalRecords: 0,
  pageNumber: 1,
  pageSize: 10,
  loading: false,
  error: null,

  filterOptions: {
    clientNames: [],
    serviceNames: [],
    statuses: [],
    engagementManagers: [],
  },
  filterOptionsLoading: false,
  filterOptionsError: null,

  statusCounts: {
    requestAccepted: 0,
    resourceIdentified: 0,
    pendingAcknowledgement: 0,
    detailedReviewCompleted: 0,
    secondLevelReviewCompleted: 0,
  },
  statusCountsLoading: false,
  statusCountsError: null,

  acknowledgeLoading: false,
  acknowledgeError: null,

  upsertServiceDetailsLoading: false,
  upsertServiceDetailsError: null,

  saveCommentsLoading: false,
  saveCommentsError: null,
};

function toArrayOrNull(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  return arr;
}

function toDateString(date) {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}${mm}${dd}`;
}

export const fetchOrders = createAsyncThunk(
  "engagementManager/fetchOrders",
  async ({ domain, ...filters }, { rejectWithValue }) => {
    try {
      const dto = {
        pageNumber: filters.pageNumber || 1,
        pageSize: filters.pageSize || 10,
        search: filters.search || null,
        clientName: toArrayOrNull(filters.clientName),
        serviceName: toArrayOrNull(filters.serviceName),
        status: toArrayOrNull(filters.status),
        statusButtons: toArrayOrNull(filters.statusButtons),
        engagementManager: toArrayOrNull(filters.engagementManager),
        engagementManagerMailId: filters.engagementManagerMailId || null,
        startDate: toDateString(filters.date?.[0]) || null,
        endDate: toDateString(filters.date?.[1]) || null,
        isCurrentUserDelegate: filters.isCurrentUserDelegate || null,
        superRegion: toArrayOrNull(filters.superRegion),
      };
      return await fetchAllPages({
        fetchPage: ({ pageNumber, pageSize }) =>
          fetchMyOrders({ ...dto, pageNumber, pageSize }, domain),
        getRows: (res) => res?.data?.data,
        getTotal: (res) => res?.data?.totalRecords,
        withRows: (res, rows) => ({ ...res, data: { ...res.data, data: rows } }),
      });
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const fetchEMFilters = createAsyncThunk(
  "engagementManager/fetchFilters",
  async (
    { domain, engagementManagerMailId, superRegion } = {},
    { rejectWithValue },
  ) => {
    try {
      return await fetchMyOrdersFilters(
        {
          engagementManagerMailId: engagementManagerMailId || null,
          superRegion: superRegion
            ? Array.isArray(superRegion)
              ? superRegion
              : [superRegion]
            : null,
        },
        domain,
      );
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const fetchStatusCounts = createAsyncThunk(
  "engagementManager/fetchStatusCounts",
  async (
    { domain, engagementManagerMailId, superRegion } = {},
    { rejectWithValue },
  ) => {
    try {
      return await fetchMyOrdersStatusCounts(
        {
          engagementManagerMailId: engagementManagerMailId || null,
          superRegion: superRegion
            ? Array.isArray(superRegion)
              ? superRegion
              : [superRegion]
            : null,
        },
        domain,
      );
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const acknowledgeOrderRequest = createAsyncThunk(
  "engagementManager/acknowledgeOrder",
  async (
    { requestIds, engagementManagerEmail, domain },
    { rejectWithValue },
  ) => {
    try {
      return await acknowledgeOrder({
        requestIds,
        engagementManagerEmail,
        domain,
      });
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const upsertServiceDetails = createAsyncThunk(
  "engagementManager/upsertServiceDetails",
  async (
    {
      requestId,
      actingUserEmail,
      updateNatureOfEngagement,
      updateTargetDeliveryDate,
      updateAccessReceivedDate,
      updateAllocationDate,
      updateDelegates,
      updateRevisedStartDate,
      updateRevisedEndDate,
      domain,
    },
    { rejectWithValue },
  ) => {
    try {
      return await upsertOrderServiceDetails(
        {
          requestId,
          actingUserEmail,
          updateNatureOfEngagement,
          updateTargetDeliveryDate,
          updateAccessReceivedDate,
          updateAllocationDate,
          updateDelegates,
          updateRevisedStartDate,
          updateRevisedEndDate,
        },
        domain,
      );
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const saveComments = createAsyncThunk(
  "engagementManager/saveComments",
  async ({ requestId, comments, domain }, { rejectWithValue }) => {
    try {
      return await upsertOrderRequestComments(requestId, comments, domain);
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

const engagementManagerSlice = createSlice({
  name: "engagementManager",
  initialState,
  reducers: {
    resetAcknowledgeError: (state) => {
      state.acknowledgeError = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        const paged = action.payload?.data;
        state.rows = paged?.data || [];
        state.totalRecords = paged?.totalRecords || 0;
        state.pageNumber = paged?.pageNumber || 1;
        state.pageSize = paged?.pageSize || 10;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchEMFilters.pending, (state) => {
        state.filterOptionsLoading = true;
        state.filterOptionsError = null;
      })
      .addCase(fetchEMFilters.fulfilled, (state, action) => {
        state.filterOptionsLoading = false;
        const opts = action.payload?.data || action.payload;
        state.filterOptions = {
          clientNames: opts?.clientNames || [],
          serviceNames: opts?.serviceNames || [],
          statuses: opts?.statuses || [],
          engagementManagers: opts?.engagementManagers || [],
        };
      })
      .addCase(fetchEMFilters.rejected, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptionsError = action.payload;
      });

    builder
      .addCase(fetchStatusCounts.pending, (state) => {
        state.statusCountsLoading = true;
        state.statusCountsError = null;
      })
      .addCase(fetchStatusCounts.fulfilled, (state, action) => {
        state.statusCountsLoading = false;
        const counts = action.payload?.data || action.payload;
        state.statusCounts = {
          requestAccepted: counts?.requestAccepted || 0,
          resourceIdentified: counts?.resourceIdentified || 0,
          pendingAcknowledgement: counts?.pendingAcknowledgement || 0,
          detailedReviewCompleted: counts?.detailedReviewCompleted || 0,
          secondLevelReviewCompleted: counts?.secondLevelReviewCompleted || 0,
        };
      })
      .addCase(fetchStatusCounts.rejected, (state, action) => {
        state.statusCountsLoading = false;
        state.statusCountsError = action.payload;
      });

    builder
      .addCase(acknowledgeOrderRequest.pending, (state) => {
        state.acknowledgeLoading = true;
        state.acknowledgeError = null;
      })
      .addCase(acknowledgeOrderRequest.fulfilled, (state) => {
        state.acknowledgeLoading = false;
      })
      .addCase(acknowledgeOrderRequest.rejected, (state, action) => {
        state.acknowledgeLoading = false;
        state.acknowledgeError = action.payload;
      });

    builder
      .addCase(upsertServiceDetails.pending, (state) => {
        state.upsertServiceDetailsLoading = true;
        state.upsertServiceDetailsError = null;
      })
      .addCase(upsertServiceDetails.fulfilled, (state) => {
        state.upsertServiceDetailsLoading = false;
      })
      .addCase(upsertServiceDetails.rejected, (state, action) => {
        state.upsertServiceDetailsLoading = false;
        state.upsertServiceDetailsError = action.payload;
      });

    builder
      .addCase(saveComments.pending, (state) => {
        state.saveCommentsLoading = true;
        state.saveCommentsError = null;
      })
      .addCase(saveComments.fulfilled, (state) => {
        state.saveCommentsLoading = false;
      })
      .addCase(saveComments.rejected, (state, action) => {
        state.saveCommentsLoading = false;
        state.saveCommentsError = action.payload;
      });
  },
});

export const { resetAcknowledgeError } = engagementManagerSlice.actions;

export default engagementManagerSlice.reducer;
